import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { Transform } from '@atlaskit/editor-prosemirror/transform';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { AiSuggestionsPlugin, Suggestion } from '../../aiSuggestionsPluginType';
import { isSuggestedEditsDefaultOn } from '../../experiments';

import { getAnchorLocalId } from './getAnchorLocalId';
import { addNoDiffAnalytics } from './analytics';
import { applyToolCalls } from './toolCalls/applyToolCalls';

/**
 * Options controlling how the diff is rendered. Kept structurally compatible with the
 * `selectSuggestion` action options so both callers can share this helper.
 */
export type ApplySuggestionDiffOptions = {
	hideDeletedDiffs?: boolean;
	scrollIntoView?: boolean;
};

/**
 * Position of the first node in `doc` matching the suggestion's anchor (or, failing that, any
 * of its tool-call target) localIds. Used as a scroll target when there is no visual diff.
 */
export function findAnchorNodePosition(doc: PMNode, suggestion: Suggestion): number | undefined {
	const findLocalIdPos = (localId: string): number | undefined => {
		let result: number | undefined;
		doc.descendants((node, pos) => {
			if (result !== undefined) {
				return false;
			}
			if (node.attrs?.localId === localId) {
				result = pos;
				return false;
			}
			return true;
		});
		return result;
	};

	const anchorLocalId = getAnchorLocalId(suggestion);
	if (anchorLocalId) {
		const anchorPos = findLocalIdPos(anchorLocalId);
		if (anchorPos !== undefined) {
			return anchorPos;
		}
	}

	for (const toolCall of suggestion.toolCalls ?? []) {
		const localIds =
			toolCall.name === 'removeNodes' ? toolCall.arguments.localIds : [toolCall.arguments.localId];
		for (const localId of localIds) {
			const pos = findLocalIdPos(localId);
			if (pos !== undefined) {
				return pos;
			}
		}
	}
	return undefined;
}

/**
 * Open every `expand` / `nestedExpand` ancestor of `pos` so the card's
 * `anchor-name` spans (emitted at the suggestion target) become layout-
 * visible instead of being hidden under the expand's collapsed content
 * wrapper, where CSS anchor positioning can't resolve them.
 *
 * Delegates to `toggleExpandRange` — it writes `expandedState` (the
 * expand's source of truth) *and* sets meta the expand plugin reads to
 * emit `Decoration.node({ forceExpandOpen })`, which is the outer-deco
 * change that makes PM call `ExpandNodeView.update()`. The range starts
 * at the top-level ancestor so `nodesBetween` definitely visits every
 * wrapping expand.
 */
export function revealAncestorExpands(
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
	doc: PMNode,
	pos: number,
	tr: Transaction,
): void {
	const $pos = doc.resolve(pos);
	if ($pos.depth === 0) {
		return;
	}

	let hasExpandAncestor = false;
	for (let depth = $pos.depth; depth > 0; depth--) {
		const node = $pos.node(depth);
		if (node.type.name === 'expand' || node.type.name === 'nestedExpand') {
			hasExpandAncestor = true;
			break;
		}
	}
	if (!hasExpandAncestor) {
		return;
	}

	api?.expand?.commands.toggleExpandRange($pos.before(1), pos, true)?.({ tr });
}

/**
 * Renders the diff for a suggestion on the given transaction and returns the
 * position to scroll to when there is no diff (so the card still lands next to
 * its anchor). Shared by the initial select, the post-regeneration re-show, and
 * the undo/redo restore path.
 */
export function applySuggestionDiff({
	api,
	suggestion,
	tr,
	options,
}: {
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined;
	options?: ApplySuggestionDiffOptions;
	suggestion: Suggestion;
	tr: Transaction;
}): number | undefined {
	let noDiffScrollPos: number | undefined;

	const anchorNodePos = findAnchorNodePosition(tr.doc, suggestion);
	if (anchorNodePos !== undefined) {
		revealAncestorExpands(api, tr.doc, anchorNodePos, tr);
	}

	const hasToolCalls = (suggestion.toolCalls?.length ?? 0) > 0;

	if (!hasToolCalls) {
		api?.showDiff?.commands.hideDiff?.({ tr });
		addNoDiffAnalytics(api?.analytics?.actions)(tr, suggestion);
		noDiffScrollPos = anchorNodePos;
	} else {
		const isolatedTr = new Transform(tr.doc);
		const appliedTr = applyToolCalls({ tr: isolatedTr, suggestion });
		const stepAttribution = suggestion.createdByAgent?.accountId
			? { agentId: suggestion.createdByAgent.accountId }
			: undefined;

		if (appliedTr.before.eq(appliedTr.doc)) {
			addNoDiffAnalytics(api?.analytics?.actions)(tr, suggestion);
			noDiffScrollPos = anchorNodePos;
		}

		api?.showDiff?.commands.showDiff({
			stepsWithAttribution: appliedTr.steps
				.map((step, i) => ({
					step: step.invert(appliedTr.docs[i]),
					stepAttribution,
				}))
				.reverse(),
			originalDoc: appliedTr.doc,
			isInverted: true,
			diffType:
				fg('platform_editor_ai_suggested_edits_smart_diff') && fg('platform_editor_ai_smart_diff')
					? 'smart'
					: 'step',
			hideDeletedDiffs: options?.hideDeletedDiffs ?? isSuggestedEditsDefaultOn(),
			scrollIntoView: options?.scrollIntoView ?? true,
			showIndicators: true,
		})({ tr });
	}

	return noDiffScrollPos;
}
